import React from "react";
import { Box, LoadingOverlay } from "@mantine/core";
import { IconPrinter } from "@tabler/icons-react";
import MobileReportHeader from "../../mobile/MobileReportHeader";
import MobileReportToolbar from "../../mobile/MobileReportToolbar";
import MobileUnsavedBar from "../../mobile/MobileUnsavedBar";
import { SheetAction } from "../../mobile/sheets/MobileActionsSheet";
import { updatedLabel } from "../ReportHeaderActions";
import RunErrorPanel from "../RunErrorPanel";
import SummaryLedgers from "./SummaryLedgers";
import { SummaryCanvasViewProps } from "./summaryCanvasTypes";
import styles from "./SummaryCanvas.module.css";

/**
 * The phone layout of a summary report: header with the chip row (date,
 * filters) and a "…" sheet with Print, the ledgers in compact form, and the
 * floating Save view / Discard bar while the view is dirty. Renders the
 * model SummaryCanvas owns.
 */
export default function MobileSummaryCanvas({
  canvas,
  definition,
  header,
  onDuplicate,
}: SummaryCanvasViewProps) {
  const { draft, view, run, data, settled } = canvas;
  const extraActions: SheetAction[] = [
    {
      key: "print",
      label: "Print",
      icon: <IconPrinter size={17} aria-hidden />,
      onClick: canvas.printer.print,
      disabled: !settled,
    },
  ];

  return (
    <>
      <MobileReportHeader
        header={header}
        onDuplicate={onDuplicate}
        duplicateLabel="Duplicate with this view"
        extraActions={extraActions}
        meta={data ? updatedLabel(data.generated_at, data.timezone) : null}
        toolbar={
          <MobileReportToolbar
            definition={definition}
            view={view}
            facets={run.data?.facets}
            resolvedRange={run.data?.date_range}
            hiddenFilters={canvas.hiddenFilters}
            onUpdate={draft.update}
          />
        }
      />
      <Box
        className={styles.body}
        data-mobile
        data-dirty={draft.dirty || undefined}
        data-empty={!run.data || undefined}
      >
        <LoadingOverlay visible={run.loading} zIndex={4} />
        {run.error ? (
          <RunErrorPanel error={run.error} onRetry={run.retry} />
        ) : (
          data && <SummaryLedgers name={header.name} data={data} compact />
        )}
      </Box>
      <MobileUnsavedBar
        dirty={draft.dirty}
        saving={draft.saving}
        onSave={() => void draft.save()}
        onDiscard={draft.discard}
      />
    </>
  );
}
